import { $api } from '../y/api';
import { YI18NString } from '../y/i18n';
import { $time } from '../y/time';
import { YCountryId, YId, YTimeString } from '../y/types';
import { CBookingId, CBookingPartId, CBookingPartStatus, CRating } from './c-booking';
import { c_cities, CCityId } from './c-city';
import { CEventId } from './c-event';
import { CSessionId } from './c-event-admin';
import { CHEventSession, CHEventVenue } from './c-event-home';
import { EMaybePaymentIntent } from './e-payment-intent';
import { SObjectId } from './s-object';
import { TTicketHomeView } from './t-ticket';

export interface CHBooking {
    id: CBookingId;
    part_id: CBookingPartId;
    quantity: number;
    purchase_quantity: number;
    status: CBookingPartStatus;
    session: CHEventSession;
    event_id: CEventId;
    event_name: YI18NString;
    city_id: CCityId;
    event_slug: string;
    venue: CHEventVenue;
    scene_name?: string;
    token: string;
    number: string;
    expires_at?: YTimeString;
    inactive_at: YTimeString;
    sales_end_at: YTimeString;
    rating?: CRating;
    picture_id: YId;
    tickets: TTicketHomeView[];
}

export function chBooking(part_id: CBookingPartId) {
    return $api.GET<CHBooking>(`c/booking_part/home_view/${part_id}`);
}

export interface CHBookingListSession {
    id: CSessionId;
    event_id: CEventId;
    is_drop_in: boolean;
    time: YTimeString;
    end_time?: YTimeString;
    sales_end: number;
    duration: number;
}

export interface CHBookingList {
    id: CBookingId;
    part_id: CBookingPartId;
    quantity: number;
    purchase_quantity: number;
    status: CBookingPartStatus;
    session: CHBookingListSession;
    event_name: YI18NString;
    city_id: CCityId;
    event_slug: string;
    venue_name: string;
    expires_at?: YTimeString;
    inactive_at: YTimeString;
    sales_end_at: YTimeString;
    rating?: CRating;
    picture_id: SObjectId;
}

export function cBookingsBook(session_id: YId, quantity: number) {
    return $api.POST<EMaybePaymentIntent<CHBookingList>>('c/bookings/book', { session_id, quantity });
}

export function cBookingsStandby(session_id: YId, quantity: number, booking_id?: CBookingId) {
    return $api.POST<CHBookingList>('c/bookings/standby', { session_id, quantity, booking_id });
}

export function cBookingBuyMore(booking_id: CBookingId, quantity: number) {
    return $api.POST<EMaybePaymentIntent<CHBookingList>>(`c/booking/${booking_id}/buy_more`, { quantity });
}

interface UnbookInfoResponse {
    is_late: boolean;
}

export function cBookingPartGetUnbookInfo(id: CBookingPartId) {
    return $api.GET<UnbookInfoResponse>(`c/booking_part/${id}/unbook`)
}

export function chBookingList(reload?: boolean) {
    return $api.cachedGET<CHBookingList[]>(`c/bookings/home_list_view`, reload);
}

export function chBookingEventLocation(b: CHBookingList | CHBooking) {
    return ['event', c_cities.maybe(b.city_id)!.slug, b.event_slug];
}

export function chBookingEventUrl(b: CHBookingList, country: YCountryId) {
    if(country == 'DE')
        return 'https://abundolive.de/' + chBookingEventLocation(b).join('/');
    else
        return 'https://abundolive.se/' + chBookingEventLocation(b).join('/');
}

export function chBookingListFilterStandby(b: CHBookingList) {
    return (b.status == 'standby' || b.status == 'standby_reserved') && !$time(b.sales_end_at).is_past;
}

export function chBookingListSortStandby(a: CHBookingList, b: CHBookingList) {
    if(a.status == 'standby_reserved') {
        if(b.status != 'standby_reserved')
            return -1;
    } else if(b.status == 'standby_reserved') {
        return 1;
    }

    return $time.compare($time(a.session.time), $time(b.session.time));
}

export function chBookingListFilterActive(b: CHBookingList) {
    return b.status == 'confirmed' && $time(b.inactive_at).is_future;
}

export function chBookingListSortActive(a: CHBookingList, b: CHBookingList) {
    return $time.compare($time(a.session.time), $time(b.session.time));
}

export function chBookingListFilterPast(b: CHBookingList) {
    return b.status == 'confirmed' && $time(b.inactive_at).is_past;
}

export function chBookingListSortPast(a: CHBookingList, b: CHBookingList) {
    return $time.compare($time(b.session.time), $time(a.session.time));
}
